import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Search, 
  Filter, 
  Calendar, 
  User, 
  Phone, 
  MapPin, 
  Edit, 
  Trash2, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Send,
  MessageSquare,
  Download,
  FilePenLine
} from 'lucide-react';
import ModalEditarAviso from '../componentes/ModalEditarAviso';
import ModalEnviarDocumentos from '../componentes/ModalEnviarDocumentos';
import MaterialesAvisoResumen from '../componentes/MaterialesAvisoResumen';
import { eliminarAviso, exportarAvisosCsv, obtenerAvisosConFiltros, obtenerTecnicosOperativa } from '../servicios/api';


const estadoColors = {
  completado: 'bg-green-500/20 text-green-300',
  documentos_enviados: 'bg-blue-500/20 text-blue-300',
  pendiente: 'bg-orange-500/20 text-orange-300',
  en_diagnostico: 'bg-cyan-500/20 text-cyan-300',
  esperando_repuesto: 'bg-amber-500/20 text-amber-300',
  presupuestado: 'bg-indigo-500/20 text-indigo-300',
  anulado: 'bg-red-500/20 text-red-400',
  cancelado: 'bg-red-500/20 text-red-400',
  programado: 'bg-purple-500/20 text-purple-300'
};

const estadoIcons = {
  completado: CheckCircle,
  documentos_enviados: Send,
  pendiente: Clock,
  en_diagnostico: AlertCircle,
  esperando_repuesto: Clock,
  presupuestado: FilePenLine,
  anulado: XCircle,
  cancelado: XCircle,
  programado: Calendar
};

export default function HistorialServicios() {
  const [avisos, setAvisos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAviso, setSelectedAviso] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [tecnicoGlobalId, setTecnicoGlobalId] = useState(localStorage.getItem('filtro_tecnico_id') || '');
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  
  const [filters, setFilters] = useState({
    fechaInicio: '',
    fechaFin: '',
    nombreEmpresa: '', // Nuevo filtro para la empresa
    tecnico: '',
    estado: ''
  });

  const obtenerAvisos = useCallback(async () => {
    console.log("Iniciando obtenerAvisos en HistorialDeServicios...");
    setCargando(true);
    setError(null);
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No autenticado.');
      setCargando(false);
      return;
    }

    try {
      const params = {
        estado: 'pendiente,en_diagnostico,esperando_repuesto,presupuestado,completado,cancelado,anulado,documentos_enviados,programado',
        historial_total: 1
      };
      const usuarioActual = JSON.parse(localStorage.getItem('usuario') || 'null');
      if (usuarioActual?.rol === 'administrador' && tecnicoGlobalId) params.tecnico_id = tecnicoGlobalId;
      const filtrosActivos = Boolean(
        filters.fechaInicio ||
        filters.fechaFin ||
        filters.tecnico ||
        filters.nombreEmpresa ||
        filters.estado
      );
      if (mesSeleccionado) {
        params.mes_aviso = mesSeleccionado;
        params.orden = 'recientes';
      } else if (filtrosActivos) {
        params.orden = 'recientes';
      } else {
        params.ultimos = 5;
      }
      if (filters.fechaInicio) params.fecha_desde = filters.fechaInicio;
      if (filters.fechaFin) params.fecha_hasta = filters.fechaFin;
      const datos = await obtenerAvisosConFiltros(params);
      if (datos.exito) {
        setAvisos(datos.avisos);
      } else {
        setError(datos.mensaje || 'No se pudieron cargar los avisos.');
      }
    } catch (err) {
      console.error('Error de conexión con el servidor al obtener avisos:', err);
      setError(err.response?.data?.mensaje || 'Error de conexión con el servidor.');
    } finally {
      setCargando(false);
    }
  }, [filters, mesSeleccionado, tecnicoGlobalId]);

  useEffect(() => {
    obtenerAvisos();
  }, [obtenerAvisos]);

  useEffect(() => {
    const actualizarTecnicoGlobal = (event) => setTecnicoGlobalId(event.detail?.tecnicoId || '');
    window.addEventListener('filtro-tecnico-cambiado', actualizarTecnicoGlobal);
    return () => window.removeEventListener('filtro-tecnico-cambiado', actualizarTecnicoGlobal);
  }, []);

  useEffect(() => {
    obtenerTecnicosOperativa()
      .then((respuesta) => setTecnicos(respuesta.tecnicos || []))
      .catch((errorTecnicos) => console.error('Error al cargar técnicos de taller:', errorTecnicos));
  }, []);

  const handleExportarCsv = async () => {
    try {
      await exportarAvisosCsv({
        fecha_desde: filters.fechaInicio,
        fecha_hasta: filters.fechaFin,
        tecnico_id: filters.tecnico,
        mes_aviso: mesSeleccionado,
        historial_total: 1,
      });
    } catch (exportError) {
      window.alert(exportError.response?.data?.mensaje || 'No se pudo exportar el CSV.');
    }
  };

  const filteredAvisos = useMemo(() => {
    const filtered = avisos.filter(aviso => {
      const matchesSearch =
        (aviso.nombre_cliente?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (aviso.telefono_cliente?.toString() ?? '').includes(searchTerm) ||
        (aviso.numero_aviso?.toString().toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (aviso.id_aviso?.toString().toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (aviso.tecnico_nombre?.toLowerCase() ?? aviso.nombre_tecnico?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (aviso.nombre_empresa?.toLowerCase() ?? '').includes(searchTerm.toLowerCase());

      const fechaAviso = aviso.fecha_cita || aviso.fecha_programada || '';
      const avisoDatePart = fechaAviso.split(' ')[0];

      const matchesFilters =
        (!filters.tecnico || String(aviso.tecnico_id || '') === String(filters.tecnico)) &&
        (!filters.estado || aviso.estado === filters.estado) &&
        (!filters.nombreEmpresa || aviso.nombre_empresa === filters.nombreEmpresa) &&
        (!filters.fechaInicio || avisoDatePart >= filters.fechaInicio) &&
        (!filters.fechaFin || avisoDatePart <= filters.fechaFin);

      return matchesSearch && matchesFilters;
    });
    return filtered;
  }, [searchTerm, filters, avisos]);

  const stats = useMemo(() => {
    const total = filteredAvisos.length;
    const estadosCompletados = ['completado', 'resuelto', 'documentos_enviados'];
    const completados = filteredAvisos.filter(s => estadosCompletados.includes(s.estado)).length;
    const pendientes = filteredAvisos.filter(s => ['pendiente', 'en_diagnostico', 'esperando_repuesto', 'presupuestado', 'documentos_enviados', 'programado'].includes(s.estado)).length;
    const anulados = filteredAvisos.filter(s => s.estado === 'anulado' || s.estado === 'cancelado').length;
    const ingresoTotal = filteredAvisos
      .filter(s => estadosCompletados.includes(s.estado))
      .reduce((sum, s) => sum + (parseFloat(s.precio_cobrado) || 0), 0);
    
    return { total, completados, pendientes, anulados, ingresoTotal };
  }, [filteredAvisos]);

  const handleEditClick = (aviso) => {
    setSelectedAviso(aviso);
    setIsEditModalOpen(true);
  };

  const handleDocsClick = (aviso) => {
    setSelectedAviso(aviso);
    setIsDocsModalOpen(true);
  };

  const handleCloseModals = () => {
    setSelectedAviso(null);
    setIsEditModalOpen(false);
    setIsDocsModalOpen(false);
  };

  const handleAvisoUpdated = () => {
    obtenerAvisos();
  };

  const handleDeleteClick = async (id_aviso) => {
    if (!window.confirm('¿Eliminar definitivamente este servicio? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const datos = await eliminarAviso(id_aviso);
      if (datos.exito) {
        if (datos.mensaje) window.alert(datos.mensaje);
        await obtenerAvisos();
      } else {
        window.alert(`Error al eliminar servicio: ${datos.mensaje}`);
      }
    } catch (err) {
      console.error('Error al eliminar aviso:', err);
      window.alert(err.response?.data?.mensaje || 'Error de conexión con el servidor al eliminar el servicio.');
    }
  };

  const ServiceCard = ({ aviso }) => {
    const EstadoIcon = estadoIcons[aviso.estado] || AlertCircle;
    
    return (
      <div className="grid w-full grid-cols-1 gap-3 rounded-lg border border-slate-700 bg-slate-800 p-4 shadow-md transition-all duration-300 hover:border-blue-500/50 md:grid-cols-2 md:items-start md:gap-4 md:p-3 xl:grid-cols-[minmax(10rem,1.1fr)_minmax(0,1.3fr)_minmax(0,1.2fr)_auto_auto]">
        
        {/* ID y Estado */}
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="font-bold text-lg text-blue-400">#{aviso.numero_aviso ?? aviso.id_aviso}</span>
          <span className={`inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${estadoColors[aviso.estado]}`}>
            <EstadoIcon className="w-3.5 h-3.5" />
            {(aviso.estado ?? '').replace('_', ' ')}
          </span>
          {aviso.tipo_registro && aviso.tipo_registro !== 'diagnostico' && <span className="max-w-full rounded bg-slate-700 px-2 py-1 text-xs text-slate-200 break-words">{aviso.tipo_registro.replace('_', ' ')}</span>}
        </div>

        {/* Cliente */}
        <div className="min-w-0">
          <div className="flex min-w-0 items-start gap-2">
            <User className="w-4 h-4 text-white" />
            <span className="min-w-0 break-words font-medium text-slate-200">{aviso.nombre_cliente}</span>
          </div>
          {aviso.nombre_empresa && (
            <div className="mt-1 min-w-0 break-words text-sm text-slate-400">
              {aviso.nombre_empresa}
            </div>
          )}
        </div>

        {/* Fecha y Técnico */}
        <div className="min-w-0">
          <div className="flex min-w-0 items-start gap-2 text-sm text-white">
            <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-white" />
            <span className="min-w-0 break-words">{new Date(aviso.fecha_cita && aviso.hora_cita ? `${aviso.fecha_cita}T${aviso.hora_cita}` : aviso.fecha_programada).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="mt-1 break-words text-sm text-slate-400">
            Técnico: {aviso.tecnico_nombre || aviso.nombre_tecnico || 'No asignado'}
          </div>
          <MaterialesAvisoResumen avisoId={aviso.id_aviso} precioCobrado={aviso.precio_cobrado} />
        </div>

        {/* Coste */}
        <div className="whitespace-nowrap xl:text-right">
          <span className="text-lg font-bold text-green-400">{parseFloat(aviso.precio_cobrado || 0).toFixed(2)}€</span>
        </div>

        {/* Acciones */}
        <div className="min-w-0">
          <div className="flex flex-wrap justify-start gap-2 xl:justify-end">
            <button 
              onClick={() => handleEditClick(aviso)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 text-sm transition-colors"
            >
              <Edit className="w-4 h-4" />
              Editar
            </button>
            {aviso.estado === 'documentos_enviados' && (
              <button 
                onClick={() => handleDocsClick(aviso)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
              >
                <Send className="w-4 h-4" />
                Enviar Docs
              </button>
            )}
            <button 
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
              onClick={() => handleDeleteClick(aviso.id_aviso)}
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (cargando) return <div className="text-center p-8 text-slate-300">Cargando historial...</div>;
  if (error) return <div className="text-center p-8 text-red-400">Error: {error}</div>;

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Historial de Servicios</h1>
              <p className="text-slate-400 mt-1">Consulta y gestiona todos los servicios realizados</p>
            </div>
            
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={handleExportarCsv}
                className="flex items-center gap-2 px-4 py-2 border border-slate-600 text-slate-200 rounded-lg hover:bg-slate-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Exportar a CSV
              </button>
              <button 
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Estadísticas
                {showStats ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {showStats && (
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-slate-200">Resumen de Actividad</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-slate-900 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.total}</div>
                <div className="text-sm text-slate-400">Total</div>
              </div>
              <div className="text-center p-4 bg-slate-900 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.completados}</div>
                <div className="text-sm text-slate-400">Completados</div>
              </div>
              <div className="text-center p-4 bg-slate-900 rounded-lg">
                <div className="text-2xl font-bold text-orange-400">{stats.pendientes}</div>
                <div className="text-sm text-slate-400">Pendientes</div>
              </div>
               <div className="text-center p-4 bg-slate-900 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.anulados}</div>
                <div className="text-sm text-slate-400">Anulados</div>
              </div>
              <div className="text-center p-4 bg-slate-900 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.ingresoTotal.toFixed(2)}€</div>
                <div className="text-sm text-slate-400">Ingresos</div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="w-full lg:w-64">
              <label htmlFor="mes-historial" className="mb-1 block text-sm font-medium text-slate-300">
                Servicios de un mes
              </label>
              <input
                id="mes-historial"
                type="month"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={mesSeleccionado}
                onChange={(e) => {
                  setMesSeleccionado(e.target.value);
                  setFilters((current) => ({ ...current, fechaInicio: '', fechaFin: '' }));
                }}
              />
              {mesSeleccionado && (
                <button
                  type="button"
                  className="mt-1 text-sm text-blue-300 hover:text-blue-200"
                  onClick={() => setMesSeleccionado('')}
                >
                  Volver a los últimos 5
                </button>
              )}
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar por cliente, teléfono, ID o técnico..."
                className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg hover:bg-slate-600 text-slate-200"
            >
              <Filter className="w-4 h-4" />
              Filtros
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-slate-900/50 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Fecha Inicio</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.fechaInicio}
                  onChange={(e) => setFilters({...filters, fechaInicio: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Fecha Fin</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.fechaFin}
                  onChange={(e) => setFilters({...filters, fechaFin: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Técnico</label>
                <select
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.tecnico}
                  onChange={(e) => setFilters({...filters, tecnico: e.target.value})}
                >
                  <option value="">Todos</option>
                  {tecnicos.map((tecnico) => <option key={tecnico.id} value={tecnico.id}>{tecnico.nombre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Empresa</label>
                <select
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.nombreEmpresa}
                  onChange={(e) => setFilters({...filters, nombreEmpresa: e.target.value})}
                >
                  <option value="">Todas</option>
                  <option value="Reparamos Hogar">Reparamos Hogar</option>
                  <option value="Kayandi">Kayandi</option>
                  {/* Añadir más opciones dinámicamente si es necesario */}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Estado</label>
                <select
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={filters.estado}
                  onChange={(e) => setFilters({...filters, estado: e.target.value})}
                >
                  <option value="">Todos</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="en_diagnostico">En diagnóstico</option>
                  <option value="esperando_repuesto">Esperando repuesto</option>
                  <option value="presupuestado">Presupuestado</option>
                  <option value="documentos_enviados">Docs. Enviados</option>
                  <option value="completado">Completado</option>
                  <option value="anulado">Anulado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-slate-200">
              Servicios Encontrados ({filteredAvisos.length})
            </h2>
            <div className="text-sm text-slate-400">
              {mesSeleccionado
                ? `Servicios creados en ${mesSeleccionado}`
                : `Últimos 5 servicios · Mostrando ${filteredAvisos.length}`}
            </div>
          </div>

          {filteredAvisos.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-slate-500 mb-4">
                <Search className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-slate-200 mb-2">No se encontraron servicios</h3>
              <p className="text-slate-400">Intenta ajustar los filtros o términos de búsqueda</p>
            </div>
          ) : (
            <div className="flex flex-col space-y-4">
              {/* Encabezados de la tabla para pantallas grandes */}
              <div className="hidden md:flex md:items-center md:justify-between md:px-4 md:py-2 md:bg-slate-700 md:rounded-t-lg md:text-slate-300 md:font-semibold md:space-x-4">
                <div className="md:w-[15%]">N.º de aviso / Estado</div>
                <div className="md:w-[25%]">Cliente / Empresa</div>
                <div className="md:w-[20%]">Fecha / Técnico</div>
                <div className="md:w-[10%] md:text-right">Coste</div>
                <div className="md:w-[30%] md:text-right">Acciones</div>
              </div>
              {filteredAvisos.map(aviso => (
                <ServiceCard key={aviso.id_aviso} aviso={aviso} />
              ))}
            </div>
          )}
        </div>

        {selectedAviso && (
          <ModalEditarAviso
            isOpen={isEditModalOpen}
            onClose={handleCloseModals}
            aviso={selectedAviso}
            onUpdateAviso={handleAvisoUpdated}
          />
        )}
        
        {selectedAviso && (
          <ModalEnviarDocumentos
            isOpen={isDocsModalOpen}
            onClose={handleCloseModals}
            aviso={selectedAviso}
            onUpdateAviso={handleAvisoUpdated}
          />
        )}
      </div>
    </div>
  );
}
